import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Braces,
  BrainCircuit,
  Cloud,
  Code2,
  LayoutDashboard,
  LifeBuoy,
  RefreshCcw,
  Smartphone,
  Workflow,
} from "lucide-react";
import { Reveal, EASE } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { Marquee } from "../components/Marquee";
import { Counter } from "../components/Counter";
import { CtaBanner } from "../components/CtaBanner";
import { marqueeItems, services, whyUs, metrics, process } from "../data/content";

const serviceIcons = [Code2, LayoutDashboard, Smartphone, Braces, Workflow, BrainCircuit, RefreshCcw, Cloud, LifeBuoy];

const heroLines = ["Technology That", "Moves Your Business", "Forward."];

const Hero = () => {
  const reduceMotion = useReducedMotion();
  const initial = reduceMotion ? false : { opacity: 0, y: 14 };
  const entrance = { duration: reduceMotion ? 0 : 0.45, ease: EASE };

  return (
    <section className="home-hero relative overflow-hidden" data-testid="home-hero">
      <div className="home-hero-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="home-hero-layout relative mx-auto grid w-full max-w-7xl items-center gap-10 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-4 lg:px-8 lg:pb-24 lg:pt-32">
        <div className="home-hero-copy relative z-10">
          <motion.div
            initial={initial}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...entrance, delay: reduceMotion ? 0 : 0.08 }}
            className="home-hero-badge inline-flex items-center gap-2.5 rounded-full border px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="pulse-dot h-1.5 w-1.5 rounded-full" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] sm:text-[11px] sm:tracking-[0.2em]">
              Custom Software · Automation · AI · Cloud · IT Solutions
            </span>
          </motion.div>

          <h1 className="home-hero-title mt-7 font-display font-semibold leading-[1.02] tracking-[-0.055em]" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <span key={i} className={`home-hero-line home-hero-line-${i + 1} block`}>
                <motion.span
                  className="block"
                  initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...entrance, delay: reduceMotion ? 0 : 0.15 + i * 0.08 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={initial}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...entrance, delay: reduceMotion ? 0 : 0.42 }}
            className="home-hero-description mt-6 max-w-2xl text-base leading-relaxed sm:mt-7 sm:text-lg"
          >
            We help businesses turn ideas, challenges, and complex processes into modern, reliable
            technology solutions — from custom software and enterprise applications to automation,
            AI integration, cloud solutions, and legacy modernization.
          </motion.p>

          <motion.div
            initial={initial}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...entrance, delay: reduceMotion ? 0 : 0.5 }}
            className="mt-8 flex flex-wrap items-center gap-3 sm:mt-10 sm:gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-start-project-btn"
              className="button-primary group inline-flex items-center gap-2 px-6 py-3.5 font-display text-sm font-semibold sm:px-7"
            >
              Start a Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/solutions"
              data-testid="hero-explore-solutions-btn"
              className="home-hero-secondary group inline-flex items-center gap-2 rounded-full border px-6 py-3.5 font-display text-sm font-semibold transition-all duration-300 sm:px-7"
            >
              Explore Our Solutions
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>

          <motion.p
            initial={initial}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...entrance, delay: reduceMotion ? 0 : 0.58 }}
            className="home-hero-support mt-9 font-mono text-[11px] leading-relaxed tracking-wide sm:mt-12 sm:text-xs"
            data-testid="hero-supporting-line"
          >
            Engineering expertise. Business understanding.{" "}
            <span>Technology built for what comes next.</span>
          </motion.p>
        </div>

        <div className="home-spark-stage relative mx-auto w-full max-w-[34rem]" aria-hidden="true">
          <div className="home-spark-orbit home-spark-orbit-outer" />
          <div className="home-spark-orbit home-spark-orbit-inner" />
          <svg className="home-spark-graphic relative z-10 h-auto w-full" viewBox="0 0 560 500" fill="none" focusable="false">
            <path className="home-link-track" d="M75 337H184L253 268H366L438 196H496" />
            <path className="home-link-track home-link-track-secondary" d="M117 169H204L245 210H325L370 165H452" />
            <path className="home-link-glint" d="M75 337H184L253 268H366L438 196H496" pathLength="1" />
            <circle className="home-link-node" cx="75" cy="337" r="8" />
            <circle className="home-link-node" cx="184" cy="337" r="8" />
            <circle className="home-link-node home-link-node-accent" cx="253" cy="268" r="12" />
            <circle className="home-link-node" cx="366" cy="268" r="8" />
            <circle className="home-link-node" cx="438" cy="196" r="8" />
            <circle className="home-link-node" cx="496" cy="196" r="8" />
            <circle className="home-link-ring" cx="253" cy="268" r="42" />
            <path className="home-link-spark" d="M257 236L237 271H254L249 299L274 260H257V236Z" />
            <circle className="home-link-node home-link-node-small" cx="117" cy="169" r="5" />
            <circle className="home-link-node home-link-node-small" cx="204" cy="169" r="5" />
            <circle className="home-link-node home-link-node-small" cx="325" cy="210" r="5" />
            <circle className="home-link-node home-link-node-small" cx="452" cy="165" r="5" />
          </svg>
          <div className="home-spark-caption home-spark-caption-one" />
          <div className="home-spark-caption home-spark-caption-two" />
        </div>
      </div>
      <div className="home-scroll-cue absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 sm:flex" aria-hidden="true">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <span className="home-scroll-line block h-8 w-px" />
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="home-intro relative py-20 lg:py-32">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
      <div className="lg:col-span-7">
        <SectionHeading
          overline="Introduction"
          title="Software Should Solve Problems — Not Create Them."
        />
        <Reveal delay={0.25}>
          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">
            Every business is different. Your technology should be designed around your processes,
            your people, and your goals — not the other way around.
          </p>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            At Spark Link IT Solutions, we work with businesses to transform ideas into software,
            manual processes into automation, legacy applications into modern platforms, and
            emerging technologies into practical business solutions.
          </p>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            We help organizations modernize operations, improve customer experiences, streamline
            internal workflows, and create digital systems that scale with growth. From custom web
            and mobile applications to AI-powered process improvements, cloud strategy, and
            application support, our work is designed to reduce friction, remove inefficiencies,
            and give teams the tools they need to perform with confidence. Whether you need a
            strategic technology partner or hands-on execution support, we combine business
            understanding with practical engineering to build solutions that deliver measurable
            impact.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.3} className="h-full">
          <div className="home-conviction card-gradient relative h-full overflow-hidden rounded-2xl border border-white/10 p-8 lg:p-10">
            <div className="home-conviction-line" aria-hidden="true" />
            <div className="relative flex h-full flex-col justify-between gap-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">
                Our conviction
              </p>
              <p className="font-display text-xl sm:text-2xl font-medium leading-snug text-white">
                "Technology should make businesses better — built around the way your business
                works."
              </p>
              <div className="flex items-center gap-3">
                <span className="spark-gradient h-px w-12" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  Spark Link IT Solutions
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="home-services relative border-t py-20 lg:py-32" data-testid="home-services-section">
    <span className="text-outline pointer-events-none absolute -top-6 right-4 hidden select-none font-display text-[10rem] font-extrabold leading-none lg:block">
      BUILD
    </span>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading
          overline="What we do"
          title="Technology Built Around Your Business."
        />
        <Reveal delay={0.2}>
          <Link
            to="/services"
            data-testid="services-view-all-link"
            className="group inline-flex items-center gap-2 font-display text-sm font-semibold text-cyan-400 transition-colors duration-300 hover:text-white"
          >
            All services
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {services.map((service, i) => {
          const Icon = serviceIcons[i];
          return (
            <Reveal key={service.title} delay={0.06 * i}>
              <Link
                to={`/services/${service.slug}`}
                data-testid={`service-card-${i}`}
                className="home-service-card group flex h-full flex-col rounded-xl border border-white/[0.08] p-6 transition-all duration-300"
              >
                <span className="home-service-icon grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-obsidian transition-all duration-300">
                  <Icon className="h-5 w-5 text-cyan-400" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">
                  {service.title}
                </h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{service.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500 transition-colors duration-300 group-hover:text-cyan-400">
                  Explore
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="home-why relative border-t bg-obsidian/30 py-20 lg:py-32" data-testid="home-why-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        overline="Why Spark Link"
        title="Technology With Purpose."
        description="Great software is not measured by how much technology it contains. It is measured by how effectively it solves a problem."
      />
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-3">
        {whyUs.map((item, i) => (
          <Reveal key={item.title} delay={0.05 * i} className="h-full">
            <div className="home-why-card flex h-full flex-col bg-void p-8 transition-colors duration-300" data-testid={`why-card-${i}`}>
              <span className="font-mono text-xs tracking-[0.2em] text-cyan-400/80">
                0{i + 1}
              </span>
              <h3 className="mt-4 font-display text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{item.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Results = () => (
  <section className="home-results relative border-t py-20 lg:py-32" data-testid="home-results-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        overline="Results"
        title="Engineering That Creates Measurable Impact."
        description="Our experience includes building and optimizing enterprise applications across learning, manufacturing, warehouse management, healthcare, reporting, automation, and other business environments."
      />
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {metrics.map((m, i) => (
          <Reveal key={m.label} delay={0.07 * i} className="h-full">
            <div
              className="home-result-card card-gradient group h-full rounded-xl border border-white/[0.08] p-8 transition-all duration-300"
              data-testid={`metric-card-${i}`}
            >
              <Counter
                to={m.value}
                prefix={m.prefix}
                suffix={m.suffix}
                className="home-result-value font-display text-5xl font-bold tracking-tight"
              />
              <p className="mt-3 font-display text-base font-semibold text-white">{m.label}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{m.desc}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.35} className="h-full">
          <div className="home-result-note flex h-full flex-col justify-between gap-6 rounded-xl border border-dashed border-white/10 p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Proven</p>
            <p className="text-sm leading-relaxed text-slate-400">
              These figures are based on documented project experience.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="home-process relative border-t bg-obsidian/30 py-20 lg:py-32" data-testid="home-process-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="How we work" title="From Idea to Impact." />
      <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {process.map((p, i) => (
          <Reveal key={p.step} delay={0.06 * i}>
            <div className="group relative" data-testid={`process-step-${p.step}`}>
              <div className="flex items-center gap-4">
                <span className="text-outline font-display text-6xl font-extrabold leading-none transition-all duration-500 group-hover:[-webkit-text-stroke:1px_rgba(0,240,255,0.55)]">
                  {p.step}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">{p.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Technology = () => (
  <section className="home-technology relative border-t py-20 lg:py-32" data-testid="home-tech-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        overline="Technology"
        title="The Right Technology for the Right Problem."
        description="We don't believe in using technology simply because it is new. We choose technologies based on the requirements, complexity, scalability, maintainability, and long-term needs of the project."
      />
      {/* <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {techCategories.map((cat, i) => (
          <Reveal key={cat.name} delay={0.06 * i} className="h-full">
            <div className="h-full rounded-xl border border-white/[0.08] bg-white/[0.02] p-7 transition-colors duration-300 hover:border-cyan-400/40" data-testid={`tech-category-${i}`}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">{cat.name}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {cat.items.map((item) => (
                  <span key={item} className="chip">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div> */}
    </div>
  </section>
);

export default function Home() {
  return (
    <main className="home-page" data-testid="home-page">
      <Hero />
      <Marquee items={marqueeItems} />
      <Intro />
      <ServicesPreview />
      <WhyUs />
      <Results />
      <Process />
      <Technology />
      <CtaBanner />
    </main>
  );
}
